// The adaptive panel (Michael, 2026-10-19): secondary content lives in a URL param and shows in the one Sheet on
// phones and as a BookPage on desktop, placed after a given page. Kinds are registered once, in one table (the
// shell's); adding a kind = one entry. The shell renders `panels.after(page)` where pages may follow and
// `panels.sheet` once; it has no per-kind or per-device branches.
// Phones: the sheet shows the open kind that comes last in the table; a dismiss closes that kind's param.
import type { ReactNode, RefObject } from "react";
import { useParam } from "@/lib/hooks";
import { BookPage } from "./page";
import { Sheet } from "./sheet";
export type PanelContext = {
  /** the param's value (?id=<value>; "1" for an on/off kind) */
  value: string;
  /** in the sheet (phones) or a page (desktop) */
  sheet: boolean;
  /** for a `<div className="d peek" ref={peekRef} />` marker (sheet only; without one it opens at half) */
  peekRef?: RefObject<HTMLDivElement | null>;
  /** open another value of the same kind (in the sheet: swap in place) */
  open: (value: string) => void;
  /** a Close button's action: on pages only (the sheet closes by its gesture and back) */
  onClose?: () => void;
};
export type PanelKind = {
  /** URL param holding it */
  param: string;
  /** data-t of its desktop page */
  t: string;
  /** data-page of its desktop page for a value (Book reveals it when it appears) */
  page: (value: string) => string;
  /** desktop placement: after this page (a data-page, or "end" = last) */
  after: string;
  label: string;
  /** extra class of the desktop page (width) */
  className: string;
  content: (c: PanelContext) => ReactNode;
};
export function usePanels(kinds: PanelKind[], mobile: boolean) {
  // one hook per table row; the table is static
  const params = kinds.map(k => ({
    kind: k,
    ...useParam(k.param)
  }));
  const openNow = params.filter(p => p.value);
  const top = openNow.at(-1) ?? null;
  return {
    /** data-page of every open desktop page, in table order (for Book) */
    pages: mobile ? [] : openNow.map(p_0 => p_0.kind.page(p_0.value!)),
    /** desktop pages that follow `page` */
    after: (page: string) => mobile ? null : openNow.filter(p_1 => p_1.kind.after === page).map(p_2 => <BookPage key={p_2.kind.param} t={p_2.kind.t} page={p_2.kind.page(p_2.value!)} id={p_2.value!} label={p_2.kind.label} className={p_2.kind.className} onClose={p_2.close}>
								{p_2.kind.content({
        value: p_2.value!,
        sheet: false,
        open: v => p_2.open(v),
        onClose: p_2.close
      })}
							</BookPage>),
    /** the one sheet (phones) */
    sheet: mobile ? <Sheet open={top ? `${top.kind.param}:${top.value}` : null} label={top?.kind.label ?? ""} stillOpen={() => params.some(p_3 => p_3.isOpen())} onDismiss={() => params.findLast(p_4 => p_4.isOpen())?.close()}>
				{peekRef => top?.kind.content({
        value: top.value!,
        sheet: true,
        peekRef,
        open: v_0 => top.open(v_0, true)
      })}
			</Sheet> : null
  };
}